import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useLocation } from "react-router-dom";

import { api } from "../../../api/apiClient";
import PageHeader from "../../../app/layout/PageHeader";
import "../../../css/buttons.css";
import "../../../css/cards.css";
import "../../../css/forms.css";
import "../../../css/tables.css";
import "./PublicRegistrationReviewPage.css";

interface PublicRegistrationSubmission {
  publicRegistrationSubmissionId: number;
  submissionReference: string;
  partyType: string;
  serviceId?: number | null;
  serviceName?: string | null;
  applicantName: string;
  payloadJson: string;
  status: string;
  submittedDate: string;
  reviewedDate?: string | null;
  reviewedBy?: string | null;
  reviewNotes?: string | null;
  createdPartyId?: number | null;
}

interface ReviewAddress {
  addressLine1?: string;
  town?: string;
  county?: string;
  postCode?: string;
  fullAddress?: string;
}

interface ReviewContact {
  contactTypeCode?: string;
  contactValue?: string;
}

interface ReviewConsent {
  consentTypeName?: string;
  question?: string;
  granted?: boolean | null;
}

interface ReviewAgreement {
  title?: string;
  accepted?: boolean | null;
}

interface ReviewPerson {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string | null;
  gender?: string;
  mobileNumber?: string;
  email?: string;
  contactMethods?: ReviewContact[];
  partyAddresses?: Array<{ address?: ReviewAddress }>;
  youthProfile?: {
    schoolName?: string;
    schoolYear?: string;
    medicalInformation?: string;
    additionalNeeds?: string;
    dietaryRequirements?: string;
    gpName?: string;
    gpContactNumber?: string;
    consents?: ReviewConsent[];
    agreements?: ReviewAgreement[];
  };
  consents?: ReviewConsent[];
  agreements?: ReviewAgreement[];
  emergencyContact?: {
    firstName?: string;
    lastName?: string;
    relationship?: string;
    mobileNumber?: string;
    email?: string;
  };
}

interface ReviewPayload {
  person?: ReviewPerson;
  organisation?: {
    displayName?: string;
    registrationNumber?: string;
    notes?: string;
    organisationTypeCode?: string;
    contactMethods?: ReviewContact[];
    partyAddresses?: Array<{ address?: ReviewAddress }>;
  };
  family?: {
    primaryContact?: ReviewPerson;
    household?: {
      name?: string;
      notes?: string;
      household?: ReviewAddress;
      partyAddress?: { address?: ReviewAddress };
    };
    members?: ReviewPerson[];
  };
  agreementResponses?: Array<{ accepted?: boolean | null }>;
}

const statuses = ["Pending", "Approved", "Rejected"];

const displayName = (person?: ReviewPerson) =>
  `${person?.firstName ?? ""} ${person?.lastName ?? ""}`.trim() || "Not provided";

const formatAddress = (address?: ReviewAddress) =>
  address?.fullAddress ||
  [address?.addressLine1, address?.town, address?.county, address?.postCode]
    .filter(Boolean)
    .join(", ") ||
  "Not provided";

const responseText = (value?: boolean | null) =>
  value === true ? "Yes" : value === false ? "No" : "Not answered";

function ContactSummary({ contacts = [] }: { contacts?: ReviewContact[] }) {
  const populated = contacts.filter((contact) => contact.contactValue?.trim());
  if (populated.length === 0) return <span>Not provided</span>;
  return (
    <div className="registration-contact-list">
      {populated.map((contact, index) => (
        <span key={`${contact.contactTypeCode}-${index}`}>
          <strong>{contact.contactTypeCode || "Contact"}:</strong> {contact.contactValue}
        </span>
      ))}
    </div>
  );
}

function YouthResponses({ person }: { person: ReviewPerson }) {
  const consents = person.youthProfile?.consents ?? person.consents ?? [];
  const agreements = person.youthProfile?.agreements ?? person.agreements ?? [];
  if (consents.length === 0 && agreements.length === 0) return null;

  return (
    <div className="registration-response-grid">
      {consents.map((consent, index) => (
        <div key={`${consent.consentTypeName}-${index}`}>
          <span>{consent.consentTypeName || consent.question || "Consent"}</span>
          <strong>{responseText(consent.granted)}</strong>
        </div>
      ))}
      {agreements.map((agreement, index) => (
        <div key={`${agreement.title}-${index}`}>
          <span>{agreement.title || "Agreement"}</span>
          <strong>{responseText(agreement.accepted)}</strong>
        </div>
      ))}
    </div>
  );
}

function RegistrationSummary({ payload }: { payload: ReviewPayload }) {
  if (payload.family) {
    const family = payload.family;
    const address = family.household?.partyAddress?.address ?? family.household?.household;
    return (
      <div className="registration-friendly-summary">
        <section className="registration-summary-section">
          <h3>Primary contact</h3>
          <dl className="registration-detail-list">
            <div><dt>Name</dt><dd>{displayName(family.primaryContact)}</dd></div>
            <div><dt>Email</dt><dd>{family.primaryContact?.email || "Not provided"}</dd></div>
            <div><dt>Mobile</dt><dd>{family.primaryContact?.mobileNumber || "Not provided"}</dd></div>
            <div><dt>Date of birth</dt><dd>{family.primaryContact?.dateOfBirth || "Not provided"}</dd></div>
          </dl>
        </section>

        <section className="registration-summary-section">
          <h3>Household</h3>
          <dl className="registration-detail-list">
            <div><dt>Name</dt><dd>{family.household?.name || "Not provided"}</dd></div>
            <div><dt>Address</dt><dd>{formatAddress(address)}</dd></div>
            {family.household?.notes && <div><dt>Notes</dt><dd>{family.household.notes}</dd></div>}
          </dl>
        </section>

        <section className="registration-summary-section">
          <h3>Family members ({family.members?.length ?? 0})</h3>
          <div className="registration-member-grid">
            {(family.members ?? []).map((member, index) => (
              <article className="registration-member-card" key={`${displayName(member)}-${index}`}>
                <h4>{displayName(member)}</h4>
                <dl className="registration-detail-list registration-detail-list--compact">
                  <div><dt>Date of birth</dt><dd>{member.dateOfBirth || "Not provided"}</dd></div>
                  <div><dt>Gender</dt><dd>{member.gender || "Not provided"}</dd></div>
                  <div><dt>School</dt><dd>{member.youthProfile?.schoolName || "Not provided"}</dd></div>
                  <div><dt>School year</dt><dd>{member.youthProfile?.schoolYear || "Not provided"}</dd></div>
                  <div><dt>Medical information</dt><dd>{member.youthProfile?.medicalInformation || "None provided"}</dd></div>
                  <div><dt>Additional needs</dt><dd>{member.youthProfile?.additionalNeeds || "None provided"}</dd></div>
                  <div><dt>Dietary requirements</dt><dd>{member.youthProfile?.dietaryRequirements || "None provided"}</dd></div>
                  <div><dt>GP</dt><dd>{[member.youthProfile?.gpName, member.youthProfile?.gpContactNumber].filter(Boolean).join(" – ") || "Not provided"}</dd></div>
                </dl>
                {member.emergencyContact && (
                  <div className="field-hint">
                    Emergency contact: {displayName(member.emergencyContact)}
                    {member.emergencyContact.relationship
                      ? ` (${member.emergencyContact.relationship})`
                      : ""}
                    {` — ${member.emergencyContact.mobileNumber || member.emergencyContact.email || "No contact method"}`}
                  </div>
                )}
                <YouthResponses person={member} />
              </article>
            ))}
            {(family.members?.length ?? 0) === 0 && <div className="field-hint">No family members submitted.</div>}
          </div>
        </section>
      </div>
    );
  }

  const party = payload.person ?? payload.organisation;
  if (!party) return <div className="notification notification--error">The submitted details could not be summarised.</div>;
  const isOrganisation = Boolean(payload.organisation);
  const name = isOrganisation
    ? payload.organisation?.displayName || "Not provided"
    : displayName(payload.person);

  return (
    <div className="registration-friendly-summary">
      <section className="registration-summary-section">
        <h3>{isOrganisation ? "Organisation details" : "Personal details"}</h3>
        <dl className="registration-detail-list">
          <div><dt>Name</dt><dd>{name}</dd></div>
          {!isOrganisation && <div><dt>Date of birth</dt><dd>{payload.person?.dateOfBirth || "Not provided"}</dd></div>}
          {!isOrganisation && <div><dt>Gender</dt><dd>{payload.person?.gender || "Not provided"}</dd></div>}
          {isOrganisation && <div><dt>Registration number</dt><dd>{payload.organisation?.registrationNumber || "Not provided"}</dd></div>}
          {isOrganisation && <div><dt>Organisation type</dt><dd>{payload.organisation?.organisationTypeCode || "Not provided"}</dd></div>}
          <div><dt>Contact details</dt><dd><ContactSummary contacts={party.contactMethods} /></dd></div>
          <div><dt>Address</dt><dd>{formatAddress(party.partyAddresses?.find((item) => item.address)?.address)}</dd></div>
          {!isOrganisation && <div><dt>Emergency contact</dt><dd>{payload.person?.emergencyContact ? `${displayName(payload.person.emergencyContact)} (${payload.person.emergencyContact.relationship || "relationship not provided"}) — ${payload.person.emergencyContact.mobileNumber || payload.person.emergencyContact.email || "no contact method"}` : "Not provided"}</dd></div>}
          {isOrganisation && payload.organisation?.notes && <div><dt>Notes</dt><dd>{payload.organisation.notes}</dd></div>}
        </dl>
      </section>
    </div>
  );
}

export default function PublicRegistrationReviewPage() {
  const location = useLocation();
  const serviceId = useMemo(() => {
    const value = new URLSearchParams(location.search).get("serviceId");
    const parsed = value ? Number(value) : null;
    return parsed && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [location.search]);
  const [status, setStatus] = useState("Pending");
  const [submissions, setSubmissions] = useState<PublicRegistrationSubmission[]>([]);
  const [selected, setSelected] = useState<PublicRegistrationSubmission | null>(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const reviewDetailRef = useRef<HTMLElement | null>(null);

  const load = useCallback(async () => {
    const result = await api<PublicRegistrationSubmission[]>(
      `/api/admin/public-registrations?status=${encodeURIComponent(status)}${
        serviceId ? `&serviceId=${serviceId}` : ""
      }`,
    );
    setSubmissions(result);
    setSelected((current) =>
      current
        ? result.find(
            (item) =>
              item.publicRegistrationSubmissionId ===
              current.publicRegistrationSubmissionId,
          ) ?? null
        : null,
    );
  }, [status, serviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const parsedPayload = useMemo<ReviewPayload | null>(() => {
    if (!selected) return null;
    try {
      return JSON.parse(selected.payloadJson) as ReviewPayload;
    } catch {
      return null;
    }
  }, [selected]);

  const openSubmission = (submission: PublicRegistrationSubmission) => {
    setSelected(submission);
    setNotes(submission.reviewNotes ?? "");

    if (window.matchMedia("(max-width: 900px)").matches) {
      window.requestAnimationFrame(() => {
        reviewDetailRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        reviewDetailRef.current?.focus({ preventScroll: true });
      });
    }
  };

  const review = async (decision: "approve" | "reject") => {
    if (!selected || busy) return;
    const verb = decision === "approve" ? "approve" : "reject";
    const warning =
      decision === "approve"
        ? "Approval will create the party and any selected service enrollment. Continue?"
        : "Reject this registration without creating any party records?";
    if (!window.confirm(warning)) return;

    setBusy(true);
    try {
      await api<void>(
        `/api/admin/public-registrations/${selected.publicRegistrationSubmissionId}/${verb}`,
        {
          method: "POST",
          body: { notes },
          notifySuccess:
            decision === "approve"
              ? "Registration approved and party created."
              : "Registration rejected.",
        },
      );
      setSelected(null);
      setNotes("");
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page-container public-registration-reviews">
      <PageHeader
        title="Public registration reviews"
        subtitle="Review staged submissions before creating parties and service enrollments."
      />

      <div className="registration-review-tabs" role="tablist" aria-label="Registration status">
        {statuses.map((item) => (
          <button
            key={item}
            type="button"
            className={`tab-button ${status === item ? "active" : ""}`}
            onClick={() => {
              setStatus(item);
              setSelected(null);
              setNotes("");
            }}
          >
            {item}
          </button>
        ))}
      </div>

      <div className={`registration-review-workspace${selected ? " has-selection" : ""}`}>
      <section className="card registration-review-list">
        <div className="card-header-row">
          <div>
            <h2>{status} submissions</h2>
            <div className="field-hint">{submissions.length} registration{submissions.length === 1 ? "" : "s"}</div>
          </div>
        </div>

        <div className="registration-submission-grid">
          {submissions.map((submission) => (
            <article
              className={`registration-submission-card${
                selected?.publicRegistrationSubmissionId === submission.publicRegistrationSubmissionId
                  ? " registration-submission-card--selected"
                  : ""
              }`}
              key={submission.publicRegistrationSubmissionId}
            >
              <div className="registration-submission-card__content">
                <div className="registration-submission-card__header">
                  <h3>{submission.applicantName || "Unnamed registration"}</h3>
                  <span className={`registration-status registration-status--${submission.status.toLowerCase()}`}>
                    {submission.status}
                  </span>
                </div>
                <div className="registration-submission-card__meta">
                  <span>{submission.serviceName ?? "No service"}</span>
                  <span>{submission.partyType}</span>
                  <time dateTime={submission.submittedDate}>
                    {new Date(submission.submittedDate).toLocaleDateString()}
                  </time>
                </div>
              </div>

              <button
                type="button"
                className="registration-submission-card__button"
                aria-pressed={selected?.publicRegistrationSubmissionId === submission.publicRegistrationSubmissionId}
                onClick={() => openSubmission(submission)}
              >
                <VisibilityIcon fontSize="small" /> Review
              </button>
            </article>
          ))}
          {submissions.length === 0 && (
            <div className="registration-submission-empty">
              There are no {status.toLowerCase()} public registrations.
            </div>
          )}
        </div>
      </section>

      {selected && (
        <section
          ref={reviewDetailRef}
          className="card registration-review-detail"
          tabIndex={-1}
          aria-label={`Review ${selected.applicantName || "registration submission"}`}
        >
          <div className="card-header-row">
            <div>
              <h2>{selected.applicantName || "Unnamed registration"}</h2>
              <div className="field-hint">Reference: {selected.submissionReference}</div>
            </div>
            <div className="registration-review-detail__header-actions">
              <span className={`registration-status registration-status--${selected.status.toLowerCase()}`}>
                {selected.status}
              </span>
              <button
                type="button"
                className="registration-review-close"
                onClick={() => {
                  setSelected(null);
                  setNotes("");
                }}
                aria-label="Close review and return to submissions"
                title="Back to submissions"
              >
                <CloseIcon fontSize="small" />
              </button>
            </div>
          </div>

          <dl className="registration-review-summary">
            <div><dt>Party type</dt><dd>{selected.partyType}</dd></div>
            <div><dt>Service</dt><dd>{selected.serviceName ?? "None"}</dd></div>
            <div><dt>Submitted</dt><dd>{new Date(selected.submittedDate).toLocaleString()}</dd></div>
            {selected.createdPartyId && <div><dt>Created party</dt><dd>{selected.createdPartyId}</dd></div>}
          </dl>

          <h3 className="registration-summary-label">Summary</h3>
          {parsedPayload ? <RegistrationSummary payload={parsedPayload} /> : (
            <div className="notification notification--error">
              The submitted details could not be converted into a summary.
            </div>
          )}

          <label className="form-label" htmlFor="registration-review-notes">Review notes</label>
          <textarea
            id="registration-review-notes"
            className="form-input"
            rows={4}
            maxLength={1000}
            value={notes}
            disabled={selected.status !== "Pending"}
            onChange={(event) => setNotes(event.target.value)}
            placeholder="Optional notes about this decision"
          />

          {selected.status === "Pending" && (
            <div className="registration-review-actions">
              <button type="button" className="action-button secondary" disabled={busy} onClick={() => review("reject")}>
                <HighlightOffIcon fontSize="small" /> Reject
              </button>
              <button type="button" className="action-button" disabled={busy} onClick={() => review("approve")}>
                <CheckCircleOutlineIcon fontSize="small" /> Approve and create party
              </button>
            </div>
          )}
        </section>
      )}
      {!selected && (
        <section className="card registration-review-placeholder" aria-label="No submission selected">
          <VisibilityIcon />
          <h2>Select a submission to review</h2>
          <p>Choose a registration from the list to see all submitted details and make a decision.</p>
        </section>
      )}
      </div>
    </div>
  );
}
